import { type ComponentPropsWithRef, forwardRef, type Ref } from 'react';
import ReactSelect, {
  components,
  type GroupBase,
  type Props as RSP,
  type SelectInstance,
} from 'react-select';
import Icon from '@/components/Icon/IconSprite';
import {
  StyledDropdownIndicator,
  StyledReactSelect,
  StyledSelectSSRPlaceholder,
} from './Select.styles';
import { type FieldErrors, type FieldValues, type Path } from 'react-hook-form';
import FormGroup, { type FormGroupProps } from '../FormGroup/FormGroup';

const DropdownIndicator = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ComponentPropsWithRef<typeof components.DropdownIndicator<Option, IsMulti, Group>>
) => (
  <components.DropdownIndicator {...props}>
    <StyledDropdownIndicator>
      <Icon className="icon" name="bx-chevron-down" />
    </StyledDropdownIndicator>
  </components.DropdownIndicator>
);

interface SelectProps<
  T extends FieldValues,
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
> extends RSP<Option, IsMulti, Group> {
  name: Path<T>;
  id: string;
  formGroupProps: Omit<FormGroupProps, 'name' | 'htmlFor' | 'required' | 'children'>;
  required?: boolean;
  errors?: FieldErrors<T>;
}

const selectComponents = { DropdownIndicator };

const Select = forwardRef(
  <
    T extends FieldValues,
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>,
  >(
    {
      id,
      isDisabled,
      required,
      formGroupProps,
      name,
      errors,
      ...rest
    }: SelectProps<T, Option, IsMulti, Group>,
    ref?: Ref<SelectInstance<Option, IsMulti, Group>>
  ) => {
    const hasError = Boolean(errors?.[name]?.message);

    return (
      <FormGroup {...formGroupProps} name={name} htmlFor={id} required={required} errors={errors}>
        <StyledSelectSSRPlaceholder isDisabled={isDisabled}>
          <StyledReactSelect isDisabled={isDisabled} hasError={hasError}>
            <ReactSelect
              {...rest}
              ref={ref}
              components={selectComponents}
              name={name}
              isDisabled={isDisabled}
              inputId={id}
              classNamePrefix="react-select"
            />
          </StyledReactSelect>
        </StyledSelectSSRPlaceholder>
      </FormGroup>
    );
  }
);

export default Select;
